// src/pages/MyMatchesPage.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { ArrowDown, ArrowUp, Check, ClipboardCheck, ClipboardList, ClipboardPen, SlidersHorizontal } from "lucide-react";
// import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import MatchList from "@/components/MatchList";
import UserMatchCard from "@/components/UserMatchCard";
import { toast } from "sonner";
import { LoadingBlock } from "@/components/LoadingState";
import { asArray } from "@/lib/asArray";
import { PaginationBar } from "@/components/PaginationBar";
import PageTutorial from "@/components/tutorial/PageTutorial";
import { useProtocolShell } from "@/contexts/ProtocolShellContext";
import { getNativePlatform } from "@/lib/runtime";
import { NativeProtocolListSkeleton } from "@/components/skeletons/NativeLoadingSkeletons";

// ✅ TanStack hooks
import {
  useOpenMatchesPaged,
  useUserMatchesPaged,
  useDeleteMatch,
  useDeleteUserMatchCascade,
} from "@/queries/matches";

// function formatDate(dateString) {
//   return new Date(dateString).toLocaleDateString("sv-SE", {
//     year: "numeric",
//     month: "short",
//     day: "numeric",
//     hour: "2-digit",
//     minute: "2-digit",
//   });
// }

const TAB_CREATED = "created-protocols";
const TAB_SAVED = "saved-protocols";
const VALID_TABS = new Set([TAB_CREATED, TAB_SAVED]);
const SAVED_SORT_DEFAULT = { key: "saved", dir: "desc" };
const CREATED_SORT_DEFAULT = { key: "created", dir: "desc" };

export default function MyMatchesPage({
  basePath = "/account/matches",
  savedDetailBasePath = "/account/matches/saved",
  showTitle = true,
  showTutorial = true,
}) {
  const { user } = useAuth();
  const isNativeAndroid = getNativePlatform() === "android";
  const { activeProtocolMatchId, clearProtocolSession } = useProtocolShell();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { tab: tabParam } = useParams();
  const activeTab = VALID_TABS.has(tabParam) ? tabParam : TAB_CREATED;
  const handleTutorialStepChange = React.useCallback(
    ({ run, stepIndex, pageName }) => {
      if (!run || pageName !== "myMatches") return;
      const desiredTab = stepIndex >= 2 ? TAB_SAVED : TAB_CREATED;
      if (activeTab !== desiredTab) {
        const nextParams = new URLSearchParams(searchParams);
        if (desiredTab === TAB_CREATED) {
          nextParams.delete("page");
          if (!nextParams.get("page_created")) nextParams.set("page_created", "1");
        } else {
          nextParams.delete("page_created");
          if (!nextParams.get("page")) nextParams.set("page", "1");
        }
        const search = nextParams.toString();
        navigate(
          {
            pathname: `${basePath}/${desiredTab}`,
            search: search ? `?${search}` : "",
          },
          { replace: true }
        );
      }
    },
    [activeTab, basePath, navigate, searchParams]
  );
  const readStoredSort = (key, fallback, allowedKeys) => {
    if (typeof window === "undefined") return fallback;
    try {
      const raw = window.localStorage.getItem(key);
      if (!raw) return fallback;
      const parsed = JSON.parse(raw);
      if (!parsed || !allowedKeys.includes(parsed.key)) return fallback;
      if (parsed.dir !== "asc" && parsed.dir !== "desc") return fallback;
      return parsed;
    } catch {
      return fallback;
    }
  };

  const [savedSort, setSavedSort] = useState(() =>
    readStoredSort("myMatches.sort.saved", SAVED_SORT_DEFAULT, ["saved", "match", "status"])
  );
  const [createdSort, setCreatedSort] = useState(() =>
    readStoredSort("myMatches.sort.created", CREATED_SORT_DEFAULT, ["created", "match", "status"])
  );
  const [createdSortDrawerOpen, setCreatedSortDrawerOpen] = useState(false);
  const [sortDrawerOpen, setSortDrawerOpen] = useState(false);
  const isIOS = useMemo(() => {
    if (typeof window === "undefined") return false;
    const ua = window.navigator.userAgent || "";
    const platform = window.navigator.platform || "";
    const maxTouchPoints = window.navigator.maxTouchPoints || 0;
    return /iPad|iPhone|iPod/.test(ua) || (platform === "MacIntel" && maxTouchPoints > 1);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem("myMatches.sort.saved", JSON.stringify(savedSort));
    } catch {}
  }, [savedSort]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem("myMatches.sort.created", JSON.stringify(createdSort));
    } catch {}
  }, [createdSort]);

  // Läs listorna via Query – cache för back-navigering; uppdatera via invalidation
  const createdPageParam = Math.max(1, Number(searchParams.get("page_created") || 1) || 1);
  const savedPageParam = Math.max(1, Number(searchParams.get("page") || 1) || 1);
  const {
    data: createdPageData,
    isLoading: loadingMatches,
    error: matchesError,
  } = useOpenMatchesPaged(
    { page: createdPageParam, pageSize: 5, sortKey: createdSort.key, sortDir: createdSort.dir },
    { staleTime: Infinity, enabled: activeTab === TAB_CREATED }
  );

  const {
    data: savedPageData,
    isLoading: loadingUserMatches,
    error: userMatchesError,
  } = useUserMatchesPaged(
    { page: savedPageParam, pageSize: 5, sortKey: savedSort.key, sortDir: savedSort.dir },
    { staleTime: Infinity, enabled: activeTab === TAB_SAVED }
  );

  // Mutationer
  // const resolveMutation = useResolveUserMatch();
  const deleteMatchMutation = useDeleteMatch();
  const deleteUserMatchCascadeMutation = useDeleteUserMatchCascade();

  // Filtrera “öppna” protokoll du skapat
  const openPageItems = asArray(createdPageData?.items);
  const openPage = Number(createdPageData?.page || createdPageParam);
  const openPageCount = Number(createdPageData?.page_count || 1);
  const umPageItems = asArray(savedPageData?.items);
  const umPage = Number(savedPageData?.page || savedPageParam);
  const umPageCount = Number(savedPageData?.page_count || 1);
  const savedTotal = Number(savedPageData?.total || 0);

  useEffect(() => {
    if (!createdPageData || createdPageParam <= openPageCount) return;
    const nextParams = new URLSearchParams(searchParams);
    if (openPageCount <= 1) nextParams.delete("page_created");
    else nextParams.set("page_created", String(openPageCount));
    setSearchParams(nextParams, { replace: true });
  }, [createdPageData, createdPageParam, openPageCount, searchParams, setSearchParams]);

  useEffect(() => {
    if (!savedPageData || savedPageParam <= umPageCount) return;
    const nextParams = new URLSearchParams(searchParams);
    if (umPageCount <= 1) nextParams.delete("page");
    else nextParams.set("page", String(umPageCount));
    setSearchParams(nextParams, { replace: true });
  }, [savedPageData, savedPageParam, searchParams, setSearchParams, umPageCount]);

  // UI-state för knapplås
  // const [resolving, setResolving] = useState({});
  // const [deleting, setDeleting] = useState({});

  // Handlers
  // const resolve = async (userMatchId, action) => {
  //   setResolving((p) => ({ ...p, [userMatchId]: true }));
  //   try {
  //     await toast.promise(resolveMutation.mutateAsync({ userMatchId, action }), {
  //       loading: "Uppdaterar…",
  //       success: "Konflikt uppdaterad",
  //       error: (e) => e?.message || "Kunde inte lösa konflikten",
  //     });
  //   } finally {
  //     setResolving((p) => ({ ...p, [userMatchId]: false }));
  //   }
  // };

  const handleDelete = async (id) => {
    await toast.promise(deleteMatchMutation.mutateAsync(id), {
      loading: "Tar bort…",
      success: "Protokoll borttaget",
      error: "Kunde inte ta bort matchen",
    });
    if (activeProtocolMatchId && String(activeProtocolMatchId) === String(id)) {
      clearProtocolSession();
    }
  };

  const handleDeleteSaved = async (userMatchId, matchId) => {
    // setDeleting((p) => ({ ...p, [userMatchId]: true }));
    // try {
    //   await toast.promise(deleteUserMatchCascadeMutation.mutateAsync(userMatchId), {
    //     loading: "Tar bort…",
    //     success: "Match borttagen",
    //     error: (e) => e?.message || "Kunde inte ta bort matchen",
    //   });
    // } finally {
    //   setDeleting((p) => ({ ...p, [userMatchId]: false }));
    // }
    await toast.promise(deleteUserMatchCascadeMutation.mutateAsync(userMatchId), {
      loading: "Tar bort…",
      success: "Match borttagen",
      error: (e) => e?.message || "Kunde inte ta bort matchen",
    });
    if (matchId && activeProtocolMatchId && String(activeProtocolMatchId) === String(matchId)) {
      clearProtocolSession();
    }
  };

  // Tab byte -> uppdatera URL
  const onTabChange = (val) => {
    const next = VALID_TABS.has(val) ? val : TAB_CREATED;
    const nextParams = new URLSearchParams(searchParams);
    if (next === TAB_CREATED) {
      nextParams.delete("page");
      if (!nextParams.get("page_created")) nextParams.set("page_created", "1");
    } else {
      nextParams.delete("page_created");
      if (!nextParams.get("page")) nextParams.set("page", "1");
    }
    const search = nextParams.toString();
    navigate(
      {
        pathname: `${basePath}/${next}`,
        search: search ? `?${search}` : "",
      },
      { replace: true }
    );
  };

  const handleCreatedPageChange = (p) => {
    const nextParams = new URLSearchParams(searchParams);
    if (p <= 1) nextParams.delete("page_created");
    else nextParams.set("page_created", String(p));
    setSearchParams(nextParams, { replace: true });
  };

  const handleSavedPageChange = (p) => {
    const nextParams = new URLSearchParams(searchParams);
    if (p <= 1) nextParams.delete("page");
    else nextParams.set("page", String(p));
    setSearchParams(nextParams, { replace: true });
  };

  const toggleSortFor = (setter, key) => {
    setter((prev) => {
      if (prev.key === key) {
        const dir = prev.dir === "desc" ? "asc" : "desc";
        return { key, dir };
      }
      return { key, dir: "desc" };
    });
  };

  const setStatusSortFor = (setter) => setter({ key: "status", dir: "desc" });

  const renderSortButtons = (sortState, setSortState, isMobile, primaryKey) => (
    <div className="native-v3-sort-options flex flex-wrap items-center gap-2">
      <Button
        variant={sortState.key === primaryKey ? "default" : "outline"}
        size={isMobile ? "sm" : "sm"}
        onClick={() => toggleSortFor(setSortState, primaryKey)}
        className="native-v3-sort-option gap-2"
        data-active={sortState.key === primaryKey}
      >
        Tillagd
        {sortState.key === primaryKey ? (
          sortState.dir === "desc" ? <ArrowDown className="h-4 w-4" /> : <ArrowUp className="h-4 w-4" />
        ) : <Check className="native-v3-sort-check h-4 w-4" />}
      </Button>
      <Button
        variant={sortState.key === "match" ? "default" : "outline"}
        size={isMobile ? "sm" : "sm"}
        onClick={() => toggleSortFor(setSortState, "match")}
        className="native-v3-sort-option gap-2"
        data-active={sortState.key === "match"}
      >
        Matchdatum
        {sortState.key === "match" ? (
          sortState.dir === "desc" ? <ArrowDown className="h-4 w-4" /> : <ArrowUp className="h-4 w-4" />
        ) : <Check className="native-v3-sort-check h-4 w-4" />}
      </Button>
      <Button
        variant={sortState.key === "status" ? "default" : "outline"}
        size={isMobile ? "sm" : "sm"}
        onClick={() => setStatusSortFor(setSortState)}
        className="native-v3-sort-option gap-2"
        data-active={sortState.key === "status"}
      >
        Status
        {sortState.key === "status" ? <Check className="h-4 w-4" /> : <Check className="native-v3-sort-check h-4 w-4" />}
      </Button>
    </div>
  );

  const renderMobileSortModal = (open, onOpenChange, title, description, content) => {
    if (isIOS) {
      return (
        <Dialog open={open} onOpenChange={onOpenChange}>
          <DialogTrigger asChild>
            <Button variant="outline" size="sm" className="native-v3-sort-trigger gap-2">
              <SlidersHorizontal className="h-4 w-4" />
              Sortera
            </Button>
          </DialogTrigger>
          <DialogContent className="native-v3-sort-dialog w-[calc(100vw-1rem)] max-w-sm p-0">
            <DialogHeader className="native-v3-sort-header border-b border-border/60 px-4 py-4 text-left">
              <DialogTitle>{title}</DialogTitle>
              <DialogDescription>{description}</DialogDescription>
            </DialogHeader>
            <div className="native-v3-sort-body px-4 pb-6 pt-4">{content}</div>
          </DialogContent>
        </Dialog>
      );
    }

    return (
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerTrigger asChild>
          <Button variant="outline" size="sm" className="native-v3-sort-trigger gap-2">
            <SlidersHorizontal className="h-4 w-4" />
            Sortera
          </Button>
        </DrawerTrigger>
        <DrawerContent className="native-v3-sort-sheet min-h-[35vh]">
          <DrawerHeader className="native-v3-sort-header">
            <DrawerTitle>{title}</DrawerTitle>
            <DrawerDescription>{description}</DrawerDescription>
          </DrawerHeader>
          <div className="native-v3-sort-body px-4 pb-6" data-vaul-no-drag="">
            {content}
          </div>
        </DrawerContent>
      </Drawer>
    );
  };

  // Swipe för mobil
  const swipeRef = useRef({ x: 0, y: 0 });
  const onTouchStart = (e) => {
    const t = e.touches?.[0];
    if (!t) return;
    swipeRef.current = { x: t.clientX, y: t.clientY };
  };
  const onTouchEnd = (e) => {
    const t = e.changedTouches?.[0];
    if (!t) return;
    const dx = t.clientX - swipeRef.current.x;
    const dy = t.clientY - swipeRef.current.y;
    if (Math.abs(dx) > 50 && Math.abs(dy) < 40) {
      // Svep vänster -> nästa tab | Svep höger -> föregående
      if (dx < 0 && activeTab === TAB_CREATED) onTabChange(TAB_SAVED);
      else if (dx > 0 && activeTab === TAB_SAVED) onTabChange(TAB_CREATED);
    }
  };

  return (
    <div className={isNativeAndroid ? "native-v3-my-matches" : undefined}>
      {showTutorial ? <PageTutorial pageName="myMatches" onStepChange={handleTutorialStepChange} /> : null}
      {showTitle ? <h1 className="text-xl font-bold mb-2">Mina matchprotokoll</h1> : null}
      <Tabs value={activeTab} onValueChange={onTabChange} className="w-full">
        <div className="mb-3">
          <TabsList
            id="myMatchesTabs"
            className="grid w-full grid-cols-2 bg-card border border-border rounded-[var(--radius)] p-0 gap-0"
          >
            <TabsTrigger
              value={TAB_CREATED}
              className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-3 py-2 text-sm"
            >
              <ClipboardPen className="w-4 h-4 mr-2" />
              Skapade
            </TabsTrigger>
            <TabsTrigger
              value={TAB_SAVED}
              className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-3 py-2 text-sm"
            >
              <ClipboardCheck className="w-4 h-4 mr-2" />
              Sparade 
            </TabsTrigger>
          </TabsList>
        </div>

        {/* Skapade (öppna) */}
        <TabsContent
          value={TAB_CREATED}
          className="mt-0"
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          <Card id="createdProtocolsCard">
            <CardHeader>
              <div className="flex items-end justify-between gap-2">
                <div className="space-y-1">
                  <CardTitle className="flex items-center gap-2">
                    <ClipboardPen className="w-5 h-5 text-muted" />
                    Skapade matchprotokoll
                  </CardTitle>
                  <CardDescription className="text-muted">Påbörjade matchprotokoll</CardDescription>
                </div>
                <div className="md:hidden">
                  {renderMobileSortModal(
                    createdSortDrawerOpen,
                    setCreatedSortDrawerOpen,
                    "Sortera skapade protokoll",
                    "Välj hur dina skapade matchprotokoll ska sorteras.",
                    renderSortButtons(createdSort, setCreatedSort, true, "created")
                  )}
                </div>
              </div>
              <div className="mt-3 hidden flex-wrap items-center gap-2 md:flex">
                <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Sortera</span>
                {renderSortButtons(createdSort, setCreatedSort, false, "created")}
              </div>
            </CardHeader>
            <CardContent>
              {loadingMatches ? (
                isNativeAndroid ? <NativeProtocolListSkeleton /> : <LoadingBlock text="Hämtar protokoll" />
              ) : matchesError ? (
                <p className="text-red-600">Kunde inte ladda skapade matcher</p>
              ) : (
                <>
                  <MatchList
                    matches={openPageItems}
                    userId={user?.id}
                    onDelete={handleDelete}
                  />
                  <PaginationBar
                    page={openPage}
                    pageCount={openPageCount}
                    onPageChange={handleCreatedPageChange}
                  />
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Sparade */}
        <TabsContent
          value={TAB_SAVED}
          className="mt-0"
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          <Card id="savedProtocolsCard">
            <CardHeader>
              <div className="flex items-end justify-between gap-2">
                <div className="space-y-1">
                  <CardTitle className="flex items-center gap-2">
                    <ClipboardCheck className="w-5 h-5 text-muted" />
                    Sparade matchprotokoll
                  </CardTitle>
                  <CardDescription className="text-muted">Färdigställda matchprotokoll</CardDescription>
                </div>
                <div className="md:hidden">
                  {renderMobileSortModal(
                    sortDrawerOpen,
                    setSortDrawerOpen,
                    "Sortera sparade protokoll",
                    "Välj hur dina sparade matchprotokoll ska sorteras.",
                    renderSortButtons(savedSort, setSavedSort, true, "saved")
                  )}
                </div>
              </div>
              <div className="mt-3 hidden flex-wrap items-center gap-2 md:flex">
                <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Sortera</span>
                {renderSortButtons(savedSort, setSavedSort, false, "saved")}
              </div>
            </CardHeader>
            <CardContent>
              {loadingUserMatches ? (
                <div className="space-y-4">
                  {isNativeAndroid ? <NativeProtocolListSkeleton /> : <LoadingBlock text="Hämtar sparade matcher" />}
                </div>
              ) : userMatchesError ? (
                <p className="text-red-600">Kunde inte ladda dina matcher</p>
              ) : savedTotal === 0 ? (
                <p className="text-muted text-center py-8">
                  Inga sparade matcher ännu
                </p>
              ) : (
                <>
                  <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 2xl:grid-cols-3">
                    {umPageItems.map((um) => (
                      <UserMatchCard
                        key={um.id || um._id}
                        userMatch={um}
                        detailBasePath={savedDetailBasePath}
                        // onResolve={resolve}
                        // loadingResolve={!!resolving[um.id || um._id]}
                        // onDelete={(userMatchId) => handleDeleteSaved(userMatchId)}
                        onDelete={handleDeleteSaved}
                      />
                    ))}
                  </div>
                  <PaginationBar
                    page={umPage}
                    pageCount={umPageCount}
                    onPageChange={handleSavedPageChange}
                  />
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}



